/** Dispute evidence uses stable evidence IDs for document-service ownership. */
import { generateClient } from 'aws-amplify/api';
import {
  DisputeEvidence,
  EvidenceStatus,
  DocumentOwnerType,
  DocumentStatus,
} from './API/types';
import {
  disputeEvidence,
  documentVersionsByRelatedEntity,
  documentVersion,
} from './API/graphql/queries';
import {
  createDisputeEvidenceUploadRecords,
  createDocumentUploadSessions,
  deleteDocumentVersion,
} from './API/graphql/mutations';
import type { ParsedResponse } from './util';

/** Evidence workflow state plus the document version used for download/deletion. */
export type EvidenceDocument = Omit<DisputeEvidence, 'dispute'> & {
  documentVersionId: string;
  available: boolean;
};

/** List only scanned, available documents and join their evidence submission state. */
export const listEvidence = async (
  disputeId: string,
  merchantUID: string,
): Promise<ParsedResponse<EvidenceDocument[]>> => {
  try {
    const client = generateClient();
    const response = await client.graphql({
      query: documentVersionsByRelatedEntity,
      variables: {
        input: {
          owner_type: DocumentOwnerType.DISPUTE,
          related_entity_type: 'DISPUTE',
          related_entity_id: disputeId,
          status: DocumentStatus.AVAILABLE,
        },
      },
    });
    if (response.errors)
      return { data: null, errors: ['Unable to fetch evidence documents'] };
    const records = await Promise.all(
      response.data.documentVersionsByRelatedEntity.documents.map(
        async ({ document }) => {
          if (
            document.owner_type !== DocumentOwnerType.DISPUTE ||
            document.related_entity_id !== disputeId ||
            document.related_entity_type !== 'DISPUTE'
          )
            throw new Error('Invalid evidence owner');
          const result = await client.graphql({
            query: disputeEvidence,
            variables: {
              dispute_id: disputeId,
              evidence_id: document.owner_id,
            },
          });
          const evidence = result.data.disputeEvidence;
          if (
            result.errors ||
            !evidence ||
            evidence.dispute_id !== disputeId ||
            evidence.merchant_uid !== merchantUID
          )
            throw new Error('Unable to fetch evidence');
          return {
            ...evidence,
            documentVersionId: document.document_version_id,
            available: true,
          };
        },
      ),
    );
    return { data: records, errors: null };
  } catch {
    return { data: null, errors: ['Unable to fetch dispute evidence'] };
  }
};

/** Prepare and upload one file. An uploaded document is not sendable until scanning completes. */
export const uploadEvidence = async (
  disputeId: string,
  merchantUID: string,
  file: File,
): Promise<ParsedResponse<EvidenceDocument>> => {
  try {
    const client = generateClient();
    const created = await client.graphql({
      query: createDisputeEvidenceUploadRecords,
      variables: {
        input: {
          dispute_id: disputeId,
          merchant_uid: merchantUID,
          files: [{ filename: file.name, mime_type: file.type }],
        },
      },
    });
    const evidence =
      created.data.createDisputeEvidenceUploadRecords.evidences.find(
        item => item.filename === file.name,
      );
    if (created.errors || !evidence?.evidence_id)
      throw new Error('Unable to create evidence');
    const prepared = await client.graphql({
      query: createDocumentUploadSessions,
      variables: {
        input: {
          documents: [
            {
              owner_type: DocumentOwnerType.DISPUTE,
              owner_id: evidence.evidence_id,
              document_type: 'DISPUTE_EVIDENCE',
              related_entity_type: 'DISPUTE',
              related_entity_id: disputeId,
              original_filename: file.name,
              content_type: file.type,
              size_bytes: file.size,
            },
          ],
        },
      },
    });
    const session = prepared.data.createDocumentUploadSessions.sessions[0];
    if (prepared.errors || !session)
      throw new Error('Unable to prepare upload');
    // Signed headers, including conditional-write headers, must be preserved exactly.
    const headers: Record<string, string> = JSON.parse(
      session.required_headers,
    );
    const upload = await fetch(session.upload_url, {
      method: session.upload_method,
      headers,
      body: file,
    });
    if (!upload.ok) throw new Error('Upload failed');
    return {
      data: {
        ...evidence,
        documentVersionId: session.document_version_id,
        available: false,
      },
      errors: null,
    };
  } catch {
    // Presigned URLs and document contents must not enter logs or error messages.
    return { data: null, errors: ['Unable to upload evidence'] };
  }
};

/** Obtain a fresh short-lived download URL rather than persisting a signed URL in UI state. */
export const downloadEvidence = async (
  documentVersionId: string,
): Promise<ParsedResponse<Blob>> => {
  try {
    const response = await generateClient().graphql({
      query: documentVersion,
      variables: { input: { document_version_id: documentVersionId } },
    });
    if (response.errors) throw new Error('Unable to retrieve evidence');
    const document = response.data.documentVersion;
    const result = await fetch(document.download_url, {
      method: document.download_method,
      headers: document.required_headers
        ? JSON.parse(document.required_headers)
        : {},
    });
    if (!result.ok) throw new Error('Download failed');
    return { data: await result.blob(), errors: null };
  } catch {
    return { data: null, errors: ['Unable to download evidence'] };
  }
};

/** Sent evidence is immutable. Document service also enforces authorization and domain rules. */
export const deleteEvidence = async (
  item: EvidenceDocument,
): Promise<ParsedResponse<boolean>> => {
  if (
    item.evidence_date ||
    !item.available ||
    item.status === EvidenceStatus.PENDING ||
    item.status === EvidenceStatus.SUCCEEDED ||
    !item.evidence_id ||
    !item.dispute_id
  )
    return {
      data: null,
      errors: ['Only available, unsent evidence can be deleted'],
    };
  try {
    const client = generateClient();
    // Another session may have submitted the evidence after this view loaded it.
    const latest = await client.graphql({
      query: disputeEvidence,
      variables: {
        dispute_id: item.dispute_id,
        evidence_id: item.evidence_id,
      },
    });
    const evidence = latest.data.disputeEvidence;
    if (
      latest.errors ||
      !evidence ||
      evidence.merchant_uid !== item.merchant_uid ||
      evidence.dispute_id !== item.dispute_id ||
      evidence.evidence_date ||
      evidence.status === EvidenceStatus.PENDING ||
      evidence.status === EvidenceStatus.SUCCEEDED
    ) {
      return { data: null, errors: ['Evidence can no longer be deleted'] };
    }
    const result = await client.graphql({
      query: deleteDocumentVersion,
      variables: {
        input: {
          document_version_id: item.documentVersionId,
          delete_allowed: true,
        },
      },
    });
    if (result.errors)
      return { data: null, errors: ['Unable to delete evidence'] };
    return { data: true, errors: null };
  } catch {
    return { data: null, errors: ['Unable to delete evidence'] };
  }
};
